import { useCallback, useEffect, useRef, useState } from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from "firebase/auth";
import { firebaseConfig } from "../firebase";
import MutationAuthModal from "../components/MutationAuthModal";
import { MUTATION_GATE_DEFAULT_COPY, formatAuthActionError, isFullySignedIn } from "../services/authGuards";

type PendingAction = {
  action: () => void | Promise<void>;
  message: string;
};

export function useMutationAuthGate() {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState(MUTATION_GATE_DEFAULT_COPY);
  const pendingRef = useRef<PendingAction | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      if (nextUser?.email) {
        setEmail(nextUser.email);
      } else {
        const cached = localStorage.getItem("buildEmail");
        if (cached) setEmail(cached);
      }
    });
    return () => unsubscribe();
  }, []);

  const runPendingAction = useCallback(async () => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    await Promise.resolve(next.action());
  }, []);

  const continueWithEmail = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPassword = password.trim();
    if (!normalizedEmail.match(/^\S+@\S+\.\S+$/)) {
      setError("Please enter a valid email.");
      return;
    }
    if (normalizedPassword.length < 6) {
      setError("Please enter a password with at least 6 characters.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser?.isAnonymous) {
        try {
          await linkWithCredential(currentUser, EmailAuthProvider.credential(normalizedEmail, normalizedPassword));
        } catch (linkError) {
          const code = typeof (linkError as any)?.code === "string" ? (linkError as any).code : "";
          if (code === "auth/email-already-in-use" || code === "auth/credential-already-in-use") {
            await signInWithEmailAndPassword(auth, normalizedEmail, normalizedPassword);
          } else {
            throw linkError;
          }
        }
      } else if (!currentUser) {
        await createUserWithEmailAndPassword(auth, normalizedEmail, normalizedPassword);
      } else if (!currentUser.email || currentUser.email.toLowerCase() !== normalizedEmail) {
        await signInWithEmailAndPassword(auth, normalizedEmail, normalizedPassword);
      }
      localStorage.setItem("buildEmail", normalizedEmail);
      setIsModalOpen(false);
      await runPendingAction();
    } catch (nextError) {
      setError(formatAuthActionError(nextError));
    } finally {
      setBusy(false);
    }
  }, [email, password, runPendingAction]);

  const continueWithGoogle = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      if (process.env.NODE_ENV !== "production") {
        console.info("[auth] Google sign-in attempt", {
          appName: auth.app.name,
          projectId: auth.app.options.projectId || firebaseConfig.projectId,
          authDomain: auth.app.options.authDomain || firebaseConfig.authDomain,
          providerId: provider.providerId,
        });
      }
      let result: any;
      if (auth.currentUser?.isAnonymous) {
        try {
          result = await linkWithPopup(auth.currentUser, provider);
        } catch (linkError) {
          const code = typeof (linkError as any)?.code === "string" ? (linkError as any).code : "";
          if (code === "auth/credential-already-in-use" || code === "auth/account-exists-with-different-credential") {
            result = await signInWithPopup(auth, provider);
          } else {
            throw linkError;
          }
        }
      } else {
        result = await signInWithPopup(auth, provider);
      }
      const accountEmail = typeof result?.user?.email === "string" ? result.user.email.trim() : "";
      if (accountEmail) {
        setEmail(accountEmail);
        localStorage.setItem("buildEmail", accountEmail.toLowerCase());
      }
      setIsModalOpen(false);
      await runPendingAction();
    } catch (nextError) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[auth] Google sign-in failed", nextError);
      }
      setError(formatAuthActionError(nextError));
    } finally {
      setBusy(false);
    }
  }, [runPendingAction]);

  const requireSignedInUser = useCallback(
    async (action: () => void | Promise<void>, customMessage?: string) => {
      if (isFullySignedIn(user)) {
        await Promise.resolve(action());
        return true;
      }
      pendingRef.current = {
        action,
        message: customMessage || MUTATION_GATE_DEFAULT_COPY,
      };
      setMessage(customMessage || MUTATION_GATE_DEFAULT_COPY);
      setError(null);
      setPassword("");
      setIsModalOpen(true);
      return false;
    },
    [user]
  );

  const requireSignedInStatus = useCallback(
    (customMessage?: string) => {
      if (isFullySignedIn(user)) return true;
      pendingRef.current = null;
      setMessage(customMessage || MUTATION_GATE_DEFAULT_COPY);
      setError(null);
      setPassword("");
      setIsModalOpen(true);
      return false;
    },
    [user]
  );

  const dismissModal = useCallback(() => {
    setIsModalOpen(false);
    setError(null);
    pendingRef.current = null;
  }, []);

  const authModal = (
    <MutationAuthModal
      open={isModalOpen}
      message={message}
      email={email}
      password={password}
      busy={busy}
      error={error}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onCancel={dismissModal}
      onContinueWithEmail={continueWithEmail}
      onContinueWithGoogle={continueWithGoogle}
    />
  );

  return {
    user,
    isFullySignedIn: isFullySignedIn(user),
    requireSignedInUser,
    requireSignedInStatus,
    authModal,
  };
}
